import { useEffect, useState, useCallback } from "react";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.matchMedia?.("(display-mode: fullscreen)").matches ||
    window.navigator.standalone === true
  );
}

function isIOSDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent);
}

/**
 * Robust install-prompt state. The browser fires `beforeinstallprompt` only once
 * per session. We stash the event and expose an install() action that can be
 * triggered from anywhere in the app. Also exposes iOS/standalone booleans so
 * the UI can adapt.
 */
export function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [ios] = useState(isIOSDevice());
  const [android] = useState(isAndroid());

  useEffect(() => {
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setInstalled(true);
    };
    const mm = window.matchMedia?.("(display-mode: standalone)");
    const onDisplayChange = () => setInstalled(isStandalone());

    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);
    mm?.addEventListener?.("change", onDisplayChange);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
      mm?.removeEventListener?.("change", onDisplayChange);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return { outcome: "unavailable" };
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      return choice; // { outcome: 'accepted' | 'dismissed', platform }
    } catch (e) {
      return { outcome: "error", error: e };
    }
  }, [deferred]);

  return {
    installable: !!deferred,     // native prompt available (Chrome/Edge/Android)
    installed,                    // running in standalone/PWA mode
    ios,                          // iOS device (needs manual Add to Home Screen)
    android,
    install,                      // triggers the native prompt (Chrome/Edge/Android)
  };
}
